// React.
import React, { useState, useEffect } from 'react';

// Material UI
import Grid from '@material-ui/core/Grid';
import TextField from '@material-ui/core/TextField';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import Button from '@material-ui/core/Button';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import SearchIcon from '@material-ui/icons/Search';
import ClearIcon from '@material-ui/icons/Clear';

// Redux
import { useDispatch, useSelector } from "react-redux";
import { preorderEventsSetFilters } from '../../../actions/preorderEventActions/PreorderEventActions';
import { RootStore } from "../../../Store";

// Translations.
import { useTranslation } from 'react-i18next';

// Types
import type { PreorderEventFilters as PreorderEventFiltersType } from '../../../actions/preorderEventActions/PreorderEventActionsTypes';

// Styles.
import useStyles from './PreorderEventFiltersStyles';

const PreorderEventFiltersComponent = () => {
  const classes = useStyles();
  const [t] = useTranslation('global');
  const dispatch = useDispatch();

  const preorderEventState = useSelector((state: RootStore) => state.preorderEvents);
  const { preorderEventFilters } = preorderEventState;

  const [localFilters, setLocalFilters] = useState<PreorderEventFiltersType>({
    name: preorderEventFilters?.name || '',
    archived: preorderEventFilters?.archived,
    releaseDate: preorderEventFilters?.releaseDate || '',
    lastPreorderDate: preorderEventFilters?.lastPreorderDate || '',
    page: 0,
    size: 10,
  });

  useEffect(() => {
    setLocalFilters({
      name: preorderEventFilters?.name || '',
      archived: preorderEventFilters?.archived,
      releaseDate: preorderEventFilters?.releaseDate || '',
      lastPreorderDate: preorderEventFilters?.lastPreorderDate || '',
      page: preorderEventFilters?.page || 0,
      size: preorderEventFilters?.size || 10,
    });
  }, [preorderEventFilters]);

  const handleFilterChange = (field: keyof PreorderEventFiltersType) => (
    event: React.ChangeEvent<{ value: unknown }>
  ) => {
    const value = event.target.value;
    let processedValue = value === '' ? undefined : value;
    
    // Handle boolean conversion for archived field
    if (field === 'archived' && processedValue !== undefined) {
      processedValue = processedValue === 'true';
    }
    
    setLocalFilters((prev: PreorderEventFiltersType) => ({
      ...prev,
      [field]: processedValue,
    }));
  };

  const handleApplyFilters = () => {
    const filtersToApply = {
      ...localFilters,
      page: 0, // Reset to first page when applying new filters
    };
    dispatch(preorderEventsSetFilters(filtersToApply));
  };

  const handleClearFilters = () => {
    const clearedFilters = {
      page: 0,
      size: 10,
    };
    setLocalFilters(clearedFilters);
    dispatch(preorderEventsSetFilters(clearedFilters));
  };

  return (
    <Paper className={classes.filtersContainer} elevation={1}>
      <Typography variant="h6" className={classes.filtersTitle}>
        {t('common.filters')}
      </Typography>
      
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            fullWidth
            label={t('preorderEvents.name')}
            value={localFilters.name || ''}
            onChange={handleFilterChange('name')}
            variant="outlined"
            size="small"
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={2}>
          <FormControl fullWidth variant="outlined" size="small">
            <InputLabel>{t('common.status')}</InputLabel>
            <Select
              value={localFilters.archived === undefined ? '' : localFilters.archived.toString()}
              onChange={handleFilterChange('archived')}
              label={t('common.status')}
            >
              <MenuItem value="">
                {t('common.all')}
              </MenuItem>
              <MenuItem value="false">
                {t('common.active')}
              </MenuItem>
              <MenuItem value="true">
                {t('common.archived')}
              </MenuItem>
            </Select>
          </FormControl>
        </Grid>
        
        <Grid item xs={12} sm={6} md={2}>
          <TextField
            fullWidth
            label={t('preorderEvents.release-date')}
            type="date"
            value={localFilters.releaseDate || ''}
            onChange={handleFilterChange('releaseDate')}
            variant="outlined"
            size="small"
            InputLabelProps={{
              shrink: true,
            }}
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={2}>
          <TextField
            fullWidth
            label={t('preorderEvents.last-preorder-date')}
            type="date"
            value={localFilters.lastPreorderDate || ''}
            onChange={handleFilterChange('lastPreorderDate')}
            variant="outlined"
            size="small"
            InputLabelProps={{
              shrink: true,
            }}
          />
        </Grid>
        
        <Grid item xs={12} md={3}>
          <div className={classes.buttonGroup}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<SearchIcon />}
              onClick={handleApplyFilters}
              className={classes.filterButton}
            >
              {t('common.search')}
            </Button>
            <Button
              variant="outlined"
              startIcon={<ClearIcon />}
              onClick={handleClearFilters}
              className={classes.filterButton}
            >
              {t('common.clear')}
            </Button>
          </div>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default PreorderEventFiltersComponent;